import { useEffect, useMemo } from "react";
import { useAppDispatch } from "../../Common/_hooks/useAppDispatch";
import { useAppSelector } from "../../Common/_hooks/useAppSelector";
import { EditLoader } from "../../UIData/_actions/UIDataActions";
import { FetchOneItem } from "../../UserData/_actions/UserDataActions";
import { ClearLab, EditActiveDesign, EditActiveOption, EditActiveProduct, EditActiveVariant, EditAutoDesignSubproduct, EditDesignName, EditLayer, EditMirrorMode, ResetActiveOptions, SelectLayer } from "../../UIData/_actions/DesignLabStoreActions";
import { GetProductLabData } from "../../UIData/_actions/DesignLabActions";
import { isBrowser } from 'browser-or-node';
import { useNavigate, useParams, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { LAB_FONTS } from "../../config";

export default function useLabInit() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate();
  const [tR] = useTranslation('routes');
  const [searchParams] = useSearchParams();

  const params = useParams<{product?: string}>();

  const selectedDesignId = useMemo(() => {
    if (!searchParams.has('design')) {
      return undefined;
    }

    const designId = Number(searchParams.get('design'));
    return Number.isNaN(designId) ? undefined : designId;
  }, [searchParams])

  const routeVariantSlug = useMemo(() => {
    return searchParams.get('variant');
  }, [searchParams])

  const routeOptionKey = useMemo(() => {
    const routeOptions: string[] = [];

    searchParams.forEach((value, key) => {
      if (key.startsWith('option_')) {
        routeOptions.push(key + '=' + value);
      }
    });

    return routeOptions.sort().join('&');
  }, [searchParams])

  const listProducts = useAppSelector(state => state.get('appData').get('products'));
  const activeDesignId = useAppSelector(state => state.get('UIData').get('designLab').get('activeDesignId'));
  const activeProductId = useAppSelector(state => state.get('UIData').get('designLab').get('activeProductId'));
  const productData = useAppSelector(state => state.get('appData').get('products').get(String(activeProductId)));
  const productDesign = useAppSelector(state => selectedDesignId ? state.get('userData').get('productDesigns').get('data').get(String(selectedDesignId)) : undefined);

  //Load design from url
  useEffect(() => {
    if (!selectedDesignId || selectedDesignId === activeDesignId) return;

    dispatch(EditLoader("design-lab_design", true));
    dispatch(FetchOneItem(selectedDesignId, 'productDesigns', ['designData']));
  }, [selectedDesignId]);

  useEffect(() => {
    if (!productDesign || productDesign.get('id') === activeDesignId) return;

    const tmpProduct = listProducts.get(String(productDesign.get('id_product')));
    if (!tmpProduct) return;

    if (productDesign.get('id_product') != productData?.get('id')) {
      navigate('/' + tR('create') + '/' + tmpProduct.get('url_slug') + '?design=' + String(productDesign.get('id')), {
        replace: true
      });
      return;
    }


    dispatch(EditActiveDesign(productDesign.get('id')));

    const variantId = productDesign.get('id_product_variant');
    const variant = variantId ? tmpProduct.get('variants').get(String(variantId)) : undefined;
    if (variant) {
      dispatch(EditActiveVariant(variant.get('slug')));
    }
    dispatch(EditDesignName(productDesign.get('name')));

    //Load layers
    let selectedLayer: string | null = null;
    Object.keys(productDesign.get('designData').layers).forEach(layerId => {
      //Select first layer
      if (!selectedLayer) {
        selectedLayer = layerId;
      }
      dispatch(EditLayer(layerId, productDesign.get('designData').layers[layerId]));
    });
    if (selectedLayer) {
      dispatch(SelectLayer(selectedLayer));
    }

    //Load mirrorModes
    if (productDesign.get('designData').mirrorModes) {
      Object.keys(productDesign.get('designData').mirrorModes).forEach(key => {
        dispatch(EditMirrorMode(key, productDesign.get('designData').mirrorModes[key]));
      });
    }

    //Load autoDesign
    if (productDesign.get('designData').autoDesignSubproducts) {
      Object.keys(productDesign.get('designData').autoDesignSubproducts).forEach(key => {
        dispatch(EditAutoDesignSubproduct(key, productDesign.get('designData').autoDesignSubproducts[key]));
      });
    }

    //Load options
    if (productDesign.get('designData').options) {
      Object.keys(productDesign.get('designData').options).forEach(key => {
        dispatch(EditActiveOption(key, productDesign.get('designData').options[key]));
      });
    }

    dispatch(EditLoader("design-lab_design", false));

  }, [productData, productDesign]);

  //Change product based on url
  useEffect(() => {
    if (!params.product) {
      dispatch(EditActiveProduct(null))
      return
    }

    const newProduct = listProducts.find(product => {
      return product.get('url_slug') === params.product;
    });

    if (!newProduct) return;

    const defaultVariant = newProduct.get('variants').first()?.get('slug') ?? 'default';
    const routeVariant = routeVariantSlug === null
      ? undefined
      : newProduct.get('variants').find(variant => variant.get('slug') === routeVariantSlug)?.get('slug');
    const nextRouteVariant = routeVariant ?? defaultVariant;

    const applyRouteOptions = () => {
      newProduct.get('availableOptionTypes').forEach(optionType => {
        const optionSlug = searchParams.get('option_' + optionType.get('slug'));

        if (optionSlug === null) {
          return;
        }

        const option = optionType.get('options').find(option => option.get('slug') === optionSlug);
        if (!option) {
          return;
        }

        dispatch(EditActiveOption(optionType.get('slug'), option.get('slug')));
      });
    };

    if (newProduct.get('id') === productData?.get('id')) {
      if (routeVariantSlug !== null) {
        dispatch(EditActiveVariant(nextRouteVariant));
      }

      if (routeOptionKey !== '') {
        dispatch(ResetActiveOptions());
        applyRouteOptions();
      }

      return;
    }

    dispatch(EditActiveProduct(newProduct.get('id')));
    dispatch(GetProductLabData(newProduct.get('slug')));

    //Always clear the lab when going in/out of fabric
    //TODO: Might implement this later for the whole lab, but with additional code to keep some data like layers
    if (productData?.get('slug') === 'fabric' || newProduct.get('slug') === 'fabric') {
      dispatch(ClearLab());
    }

    if (newProduct.get('slug') === 'fabric') {
      //FabricLab will handle its own default variant when the route doesn't specify one.
      if (routeVariantSlug !== null) {
        dispatch(EditActiveVariant(nextRouteVariant));
      }

      if (routeOptionKey !== '') {
        dispatch(ResetActiveOptions());
        applyRouteOptions();
      }

      return;
    }

    if (routeVariantSlug !== null) {
      dispatch(EditActiveVariant(nextRouteVariant));
    }

    //Don't reset anything if it's the same product or if we're loading a design
    if (newProduct.get('id') === productData?.get('id') || activeDesignId) {
      return;
    }

    if (routeVariantSlug === null) {
      dispatch(EditActiveVariant(nextRouteVariant));
    }

    //Reset options
    dispatch(ResetActiveOptions());
    applyRouteOptions();
  }, [params.product, listProducts, routeOptionKey, routeVariantSlug]);

  useEffect(() => {
    if (!isBrowser) return;

    const WebFontLoader = require('webfontloader');

    WebFontLoader.load({
      google: {
        families: LAB_FONTS
      },
      active: () => {
        //On load
      },
      classes: false
    });
  }, []);
}
